import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";
import { ArrowLeft, Loader2, TrendingUp, AlertTriangle, ChevronRight, Target } from "lucide-react";
import { api } from "@/lib/api";
import StudyGoals from "@/components/StudyGoals";
import WeeklySummary from "@/components/WeeklySummary";

export default function Progress() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.progress().then(setData).catch(() => setData({ attempts: [], review: [] })).finally(() => setLoading(false));
  }, []);

  const chartData = (data?.attempts || []).map((a, i) => ({
    idx: i + 1,
    pct: a.pct,
    label: new Date(a.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }),
    title: a.title,
  }));

  const avg = chartData.length ? Math.round(chartData.reduce((s, d) => s + d.pct, 0) / chartData.length) : 0;
  const best = chartData.length ? Math.max(...chartData.map((d) => d.pct)) : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      <Link to="/" data-testid="progress-back-link" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Torna ai materiali
      </Link>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex items-center gap-2 mb-6">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <TrendingUp className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight leading-none">Progressi</h1>
            <p className="text-sm text-muted-foreground mt-1">L'andamento dei tuoi quiz nel tempo</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : chartData.length === 0 ? (
          <div className="space-y-6">
            <div className="text-center py-16 border border-dashed border-border rounded-2xl">
              <Target className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">Completa qualche quiz per vedere qui i tuoi progressi.</p>
            </div>
            <StudyGoals />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              <Stat label="Quiz svolti" value={chartData.length} />
              <Stat label="Media" value={`${avg}%`} accent />
              <Stat label="Miglior punteggio" value={`${best}%`} />
            </div>

            <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-sm" data-testid="progress-chart">
              <h3 className="font-display font-bold mb-4">Punteggio nel tempo</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="idx" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", fontSize: 13 }}
                    labelFormatter={(v) => `Quiz #${v}`}
                    formatter={(val, _n, p) => [`${val}%`, p?.payload?.title || "Punteggio"]}
                  />
                  <ReferenceLine y={avg} stroke="hsl(var(--primary))" strokeDasharray="4 4" strokeOpacity={0.5} />
                  <Line type="monotone" dataKey="pct" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(var(--primary))" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <WeeklySummary />

            <div>
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                <AlertTriangle className="h-3.5 w-3.5" /> Ripasso programmato — argomenti da rivedere
              </div>
              <div className="space-y-2" data-testid="review-list">
                {data.review.filter((r) => r.avg_pct < 80).length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-5 text-center text-sm text-muted-foreground">
                    Ottimo! Nessun argomento debole per ora. Continua così! 🎯
                  </div>
                ) : (
                  data.review.filter((r) => r.avg_pct < 80).map((r) => (
                    <Link
                      key={r.material_id}
                      to={`/material/${r.material_id}`}
                      data-testid={`review-item-${r.material_id}`}
                      className="group flex items-center justify-between gap-3 bg-card border border-border rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: r.subject_color }} />
                          <span className="text-xs text-muted-foreground truncate">{r.subject_name}</span>
                        </div>
                        <p className="font-medium text-sm truncate">{r.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{r.attempts_count} {r.attempts_count === 1 ? "tentativo" : "tentativi"} · ultimo {r.last_pct}%</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`font-display font-bold text-lg ${r.avg_pct >= 50 ? "text-amber-600" : "text-destructive"}`}>{r.avg_pct}%</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
            <StudyGoals />
          </div>
        )}
      </motion.div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-accent border-primary/20" : "bg-card border-border"}`}>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`font-display text-2xl font-extrabold ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
